import { useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { useOabPartnershipModal } from '../../hooks/useOabPartnershipModal';
import type { OabPartnershipModalProps } from './types';
import * as S from './styles';

export function OabPartnershipModal({
  isOpen,
  urlCoupon,
  onApplyCoupon,
  onClose,
  onNotice,
}: OabPartnershipModalProps) {
  const {
    step,
    oabNumber,
    setOabNumber,
    fieldError,
    isSubmitting,
    onSubmit,
    onConfirmIdentity,
    onRejectIdentity,
    maskedName,
    inscriptionYear,
    onContinueWithoutCoupon,
    onContinueRegistration,
    previousCoupon,
    resolvedCoupon,
    notice,
  } = useOabPartnershipModal({ isOpen, urlCoupon, onApplyCoupon, onClose });

  useEffect(() => {
    onNotice?.(notice);
  }, [notice, onNotice]);

  if (step === 'input' || step === 'not_found') {
    const hasError = step === 'not_found' || Boolean(fieldError);

    return (
      <S.StyledDialog open={isOpen} onClose={onContinueWithoutCoupon}>
        <S.Head>
          <div>
            <S.Title>Confirme sua parceria CAA-PR</S.Title>
            <S.Description>
              Informe seu registro na OAB/PR pra liberarmos o desconto exclusivo da parceria.
            </S.Description>
          </div>
          <S.CloseButton onClick={onContinueWithoutCoupon} aria-label="Fechar">
            <CloseIcon />
          </S.CloseButton>
        </S.Head>

        <S.Body>
          <S.Fields>
            <S.Field>
              <S.Label htmlFor="oab-partnership-number">Número da OAB/PR</S.Label>
              <S.Input
                id="oab-partnership-number"
                inputMode="numeric"
                placeholder="Ex.: 12345"
                value={oabNumber}
                $hasError={hasError}
                onChange={event => setOabNumber(event.target.value)}
              />
            </S.Field>
          </S.Fields>

          {hasError ? (
            <>
              <S.ErrorNotice role="alert">
                <ErrorOutlineIcon />
                <span>
                  {fieldError ??
                    'Não encontramos esse registro na OAB/PR. Confira o número da OAB — ele precisa bater com o que está lá.'}
                </span>
              </S.ErrorNotice>
              <S.HelperNote>
                &quot;Continuar sem cupom&quot; ainda garante o Plano Degustação OAB-PR — você não
                fica sem nenhum benefício da parceria.
              </S.HelperNote>
            </>
          ) : (
            <S.HelperNote>
              Se preferir não confirmar agora, você pode fechar esta janela e continuar o cadastro
              sem o desconto de parceria.
            </S.HelperNote>
          )}
        </S.Body>

        <S.Foot $sideBySide>
          <S.SecondaryButton type="button" onClick={onContinueWithoutCoupon}>
            Continuar sem cupom
          </S.SecondaryButton>
          <S.PrimaryButton type="button" disabled={isSubmitting} onClick={onSubmit}>
            {hasError ? 'Corrigir e verificar novamente' : 'Confirmar parceria'}
          </S.PrimaryButton>
        </S.Foot>
      </S.StyledDialog>
    );
  }

  if (step === 'confirm') {
    return (
      <S.StyledDialog open={isOpen} onClose={onContinueWithoutCoupon}>
        <S.Head>
          <div>
            <S.Title>Confirme sua identidade</S.Title>
          </div>
          <S.CloseButton onClick={onContinueWithoutCoupon} aria-label="Fechar">
            <CloseIcon />
          </S.CloseButton>
        </S.Head>

        <S.Body>
          <S.Fields>
            <S.Field>
              <S.Label>Nome</S.Label>
              <S.CouponComparison>{maskedName}</S.CouponComparison>
            </S.Field>
            <S.Field>
              <S.Label>Inscrição</S.Label>
              <S.CouponComparison>
                OAB/PR {oabNumber} — desde {inscriptionYear}
              </S.CouponComparison>
            </S.Field>
          </S.Fields>

          <S.HelperNote>
            Mostramos o nome parcialmente por segurança. Se não reconhecer, provavelmente há um
            dígito errado no número.
          </S.HelperNote>
        </S.Body>

        <S.Foot $sideBySide>
          <S.SecondaryButton type="button" onClick={onRejectIdentity}>
            Não é meu registro
          </S.SecondaryButton>
          <S.PrimaryButton type="button" onClick={onConfirmIdentity}>
            Sim, sou eu
          </S.PrimaryButton>
        </S.Foot>
      </S.StyledDialog>
    );
  }

  if (step === 'eligible' || step === 'range_mismatch') {
    return (
      <S.StyledDialog open={isOpen} onClose={onClose}>
        <S.Body>
          <S.ResultState>
            <S.ResultIcon $variant="success">
              <CheckCircleOutlineIcon />
            </S.ResultIcon>
            <S.ResultTitle>Parceria CAA-PR confirmada</S.ResultTitle>
            {step === 'eligible' ? (
              <S.ResultMessage>
                Cupom aplicado — o desconto exclusivo da parceria já está no seu cadastro.
              </S.ResultMessage>
            ) : (
              <S.CouponComparison>
                O cupom <strong>{previousCoupon}</strong> não corresponde ao seu tempo de OAB —
                aplicamos <strong>{resolvedCoupon}</strong> automaticamente.
              </S.CouponComparison>
            )}
          </S.ResultState>
        </S.Body>
      </S.StyledDialog>
    );
  }

  if (step === 'no_special_plan') {
    return (
      <S.StyledDialog open={isOpen} onClose={onContinueRegistration}>
        <S.Body>
          <S.ResultState>
            <S.ResultIcon $variant="success">
              <CheckCircleOutlineIcon />
            </S.ResultIcon>
            <S.ResultTitle>Registro CAA-PR confirmado</S.ResultTitle>
            <S.ResultMessage>
              Esse desconto é só pra faixas específicas de tempo de OAB, e a sua ficou fora
              delas. Mas como advogado CAA-PR, você ainda garante desconto exclusivo nos
              planos base.
            </S.ResultMessage>
          </S.ResultState>
        </S.Body>
        <S.Foot>
          <S.PrimaryButton type="button" onClick={onContinueRegistration} style={{ width: '100%' }}>
            Continuar cadastro
          </S.PrimaryButton>
        </S.Foot>
      </S.StyledDialog>
    );
  }

  return (
    <S.StyledDialog open={isOpen} onClose={onClose}>
      <S.Body>
        <S.ResultState>
          <S.ResultIcon $variant="error">
            <ErrorOutlineIcon />
          </S.ResultIcon>
          <S.ResultTitle>Consulta à OAB indisponível</S.ResultTitle>
          <S.ResultMessage>
            Não conseguimos confirmar seu registro agora. Você já segue com o benefício de entrada
            da parceria — tente de novo mais tarde ou fale com o suporte.
          </S.ResultMessage>
        </S.ResultState>
      </S.Body>
    </S.StyledDialog>
  );
}

export default OabPartnershipModal;
